import React, { useMemo, useState } from 'react';
import { Modal } from '../ui/Modal';
import { useAuth } from '../../contexts/AuthContext';
import {
  getPasswordRequirementStatus,
  validatePasswordStrength,
} from '../../utils/passwordValidation';

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
}

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  open,
  onClose,
}) => {
  const { changePassword } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const requirementStatus = useMemo(
    () => getPasswordRequirementStatus(newPassword),
    [newPassword]
  );

  const passwordsMatch =
    newPassword.length > 0 && newPassword === confirmPassword;
  const passwordValidation = useMemo(
    () => validatePasswordStrength(newPassword),
    [newPassword]
  );
  const canSubmit =
    passwordValidation.isValid && passwordsMatch && !isSubmitting;

  const resetForm = () => {
    setNewPassword('');
    setConfirmPassword('');
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setError('');
    setSuccessMessage('');
    setIsSubmitting(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!passwordValidation.isValid) {
      setError(passwordValidation.errors[0] || 'Password does not meet requirements.');
      return;
    }

    if (!passwordsMatch) {
      setError('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await changePassword(newPassword);
      if (!result.success) {
        setError(result.errorMessage || 'Failed to change password.');
        return;
      }

      setSuccessMessage('Your password has been updated successfully.');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change password.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const requirementItem = (met: boolean, label: string) => (
    <li
      className="text-sm flex items-center gap-2"
      style={{ color: met ? '#22c55e' : 'var(--color-text-muted)' }}
    >
      <span aria-hidden="true">{met ? '✓' : '○'}</span>
      <span>{label}</span>
    </li>
  );

  return (
    <Modal
      open={open}
      title="Change Password"
      onClose={handleClose}
      maxWidthClassName="max-w-md"
      closeOnBackdropClick={false}
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            className="btn-secondary"
            disabled={isSubmitting}
          >
            {successMessage ? 'Close' : 'Cancel'}
          </button>
          {!successMessage && (
            <button
              type="submit"
              form="change-password-form"
              className="btn-primary"
              disabled={!canSubmit}
            >
              {isSubmitting ? 'Saving...' : 'Update Password'}
            </button>
          )}
        </>
      }
    >
      {successMessage ? (
        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          {successMessage}
        </p>
      ) : (
        <form id="change-password-form" onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div
              className="px-4 py-3 rounded-lg text-sm"
              style={{
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: '#ef4444',
              }}
            >
              {error}
            </div>
          )}

          <div>
            <label
              htmlFor="newPassword"
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--color-text-primary)' }}
            >
              New password
            </label>
            <div className="relative">
              <input
                id="newPassword"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="input-field pr-12"
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowNewPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-sm"
                style={{ color: 'var(--color-text-muted)' }}
              >
                {showNewPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Re-type new password
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input-field pr-12"
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-sm"
                style={{ color: 'var(--color-text-muted)' }}
              >
                {showConfirmPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            {confirmPassword.length > 0 && !passwordsMatch && (
              <p className="text-sm mt-1" style={{ color: '#ef4444' }}>
                Passwords do not match.
              </p>
            )}
          </div>

          <div
            className="rounded-lg p-4"
            style={{
              backgroundColor: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border)',
            }}
          >
            <p
              className="text-sm font-medium mb-2"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Password requirements
            </p>
            <ul className="space-y-1">
              {requirementItem(requirementStatus.length, 'At least 10 characters')}
              {requirementItem(requirementStatus.lowercase, 'At least one lowercase letter')}
              {requirementItem(requirementStatus.uppercase, 'At least one uppercase letter')}
              {requirementItem(requirementStatus.number, 'At least one number')}
              {requirementItem(requirementStatus.symbol, 'At least one symbol')}
            </ul>
          </div>
        </form>
      )}
    </Modal>
  );
};
